/**
 * GESTION ÉNERGIES - HAL 3000
 * Consommation du JOUR en TRÈS GROS
 * Électricité (Enedis) + Gaz (Viessmann) + Solaire (Growatt)
 * Optimisé pour iPad Air/Pro
 */
import { useState, useEffect, useCallback } from 'react';
import Layout from '@/components/Layout';
import { api } from '@/App';
import { 
  Sun, Zap, Flame, TrendingUp, TrendingDown, RefreshCw, Gauge
} from 'lucide-react';

const Energy = () => {
  const [solarData, setSolarData] = useState(null);
  const [enedisData, setEnedisData] = useState(null);
  const [vicareData, setVicareData] = useState(null);
  const [prices, setPrices] = useState({ electricity_price_kwh: 0.1883, gas_price_kwh: 0.12 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [solarRes, enedisRes, vicareRes, pricesRes] = await Promise.all([
        api.get('/integrations/solar/realtime'),
        api.get('/integrations/enedis/summary'),
        api.get('/integrations/vicare/status'),
        api.get('/energy/prices').catch(e => ({ data: null })),
      ]);
      
      if (solarRes.data && !solarRes.data.error) setSolarData(solarRes.data);
      if (enedisRes.data?.success) setEnedisData(enedisRes.data);
      if (vicareRes.data?.success) setVicareData(vicareRes.data);
      if (pricesRes.data?.success) setPrices(pricesRes.data.prices);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 300000); // 5 minutes (économie API)
    return () => clearInterval(interval);
  }, [loadData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setTimeout(() => setRefreshing(false), 1000);
  };

  const ELECTRICITY_PRICE = prices.electricity_price_kwh;
  const GAS_PRICE_KWH = prices.gas_price_kwh;

  const gasTodayKwh = vicareData?.gas_consumption?.total?.today || 0;
  const gasTodayCost = gasTodayKwh * GAS_PRICE_KWH;
  const burnerActive = vicareData?.burner?.active;

  const elecYesterdayKwh = enedisData?.daily?.consumption?.yesterday_kwh || 0;
  const elecTodayCost = elecYesterdayKwh * ELECTRICITY_PRICE;

  const solarYesterdayKwh = enedisData?.daily?.production?.yesterday_kwh || 0;
  const solarTodaySavings = solarYesterdayKwh * ELECTRICITY_PRICE;

  const maxPower = enedisData?.daily?.max_power?.overall_max_power || 0;
  const growattPower = solarData?.value || 0;

  const totalCostYesterday = elecTodayCost + gasTodayCost - solarTodaySavings;

  return (
    <Layout title="Gestion Énergies">
      <div className="h-full flex flex-col gap-2 p-1">
        
        {/* Header */}
        <div className="flex items-center justify-between flex-shrink-0">
          <div className="text-lg font-bold text-cyan-400 font-mono">
            BILAN ÉNERGÉTIQUE (VEILLE)
          </div>
          <button 
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-cyan-900/50 border border-cyan-500 rounded-lg text-cyan-400 hover:bg-cyan-900 text-sm"
            data-testid="energy-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span className="font-mono">ACTUALISER</span>
          </button>
        </div>

        {/* PANNEAUX - 3 colonnes */}
        <div className="flex-1 grid grid-cols-3 gap-3 min-h-0 overflow-hidden">
          
          {/* ÉLECTRICITÉ */}
          <div className="bg-gradient-to-br from-blue-900/40 to-blue-800/20 border-2 border-blue-500 rounded-xl p-3 flex flex-col min-h-0 overflow-hidden" data-testid="elec-panel">
            <div className="flex items-center gap-2 mb-2 flex-shrink-0">
              <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <div className="min-w-0">
                <div className="text-xl font-bold text-blue-400 font-mono">ÉLECTRICITÉ</div>
                <div className="text-xs text-gray-500 truncate">Total Energies / Enedis</div>
              </div>
            </div>
            
            <div className="flex-1 flex flex-col justify-center min-h-0">
              <div className="text-center">
                <div className="text-5xl font-bold text-blue-300 font-mono leading-tight">
                  {elecYesterdayKwh.toFixed(1)}
                </div>
                <div className="text-xl text-blue-400 font-mono">kWh</div>
              </div>
              
              <div className="mt-3 text-center">
                <div className="text-3xl font-bold text-red-400 font-mono">
                  {elecTodayCost.toFixed(2)}€
                </div>
                <div className="text-sm text-gray-500 font-mono">
                  @ {ELECTRICITY_PRICE}€/kWh
                </div>
              </div>
            </div>

            <div className="mt-2 p-2 bg-black/40 rounded-lg border border-blue-500/50 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <Gauge className="w-4 h-4 text-yellow-400" />
                  <span className="text-sm text-gray-400 font-mono">P.MAX</span>
                </div>
                <div className="text-xl font-bold text-yellow-400 font-mono">
                  {maxPower} W
                </div>
              </div>
            </div>
          </div>

          {/* GAZ VIESSMANN */}
          <div className="bg-gradient-to-br from-orange-900/40 to-orange-800/20 border-2 border-orange-500 rounded-xl p-3 flex flex-col min-h-0 overflow-hidden" data-testid="gas-panel">
            <div className="flex items-center gap-2 mb-2 flex-shrink-0">
              <div className="w-10 h-10 rounded-full bg-orange-600 flex items-center justify-center flex-shrink-0">
                <Flame className="w-6 h-6 text-white" />
              </div>
              <div className="min-w-0">
                <div className="text-xl font-bold text-orange-400 font-mono">GAZ</div>
                <div className="text-xs text-gray-500 truncate">Chaudière Viessmann</div>
              </div>
            </div>
            
            <div className="flex-1 flex flex-col justify-center min-h-0">
              <div className="text-center">
                <div className="text-5xl font-bold text-orange-300 font-mono leading-tight">
                  {gasTodayKwh.toFixed(1)}
                </div>
                <div className="text-xl text-orange-400 font-mono">kWh</div>
              </div>
              
              <div className="mt-3 text-center">
                <div className="text-3xl font-bold text-red-400 font-mono">
                  {gasTodayCost.toFixed(2)}€
                </div>
                <div className="text-sm text-gray-500 font-mono">
                  @ {GAS_PRICE_KWH}€/kWh
                </div>
              </div>
            </div>

            <div className="mt-2 p-2 bg-black/40 rounded-lg border border-orange-500/50 flex-shrink-0">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400 font-mono">BRÛLEUR</span>
                <div className={`px-3 py-1 rounded-full text-sm font-bold font-mono ${
                  burnerActive 
                    ? 'bg-orange-500 text-white' 
                    : 'bg-gray-700 text-gray-400'
                }`}>
                  {burnerActive ? 'ACTIF' : 'ARRÊT'}
                </div>
              </div>
              {burnerActive && (
                <div className="mt-1 text-center text-lg font-bold text-orange-400 font-mono">
                  {vicareData.burner.modulation}% modulation
                </div>
              )}
            </div>
          </div>

          {/* SOLAIRE */}
          <div className="bg-gradient-to-br from-green-900/40 to-green-800/20 border-2 border-green-500 rounded-xl p-3 flex flex-col min-h-0 overflow-hidden" data-testid="solar-panel">
            <div className="flex items-center gap-2 mb-2 flex-shrink-0">
              <div className="w-10 h-10 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0">
                <Sun className="w-6 h-6 text-yellow-300" />
              </div>
              <div className="min-w-0">
                <div className="text-xl font-bold text-green-400 font-mono">SOLAIRE</div>
                <div className="text-xs text-gray-500 truncate">Production injectée</div>
              </div>
            </div>
            
            <div className="flex-1 flex flex-col justify-center min-h-0">
              <div className="text-center">
                <div className="text-5xl font-bold text-green-300 font-mono leading-tight">
                  {solarYesterdayKwh.toFixed(1)}
                </div>
                <div className="text-xl text-green-400 font-mono">kWh</div>
              </div>
              
              <div className="mt-3 text-center">
                <div className="text-3xl font-bold text-green-400 font-mono">
                  -{solarTodaySavings.toFixed(2)}€
                </div>
                <div className="text-sm text-gray-500 font-mono">
                  REVENUS INJECTION
                </div>
              </div>
            </div>

            <div className="mt-2 p-2 bg-black/40 rounded-lg border border-green-500/50 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <Sun className="w-4 h-4 text-yellow-400" />
                  <span className="text-sm text-gray-400 font-mono">GROWATT</span>
                </div>
                <div className="text-xl font-bold text-yellow-400 font-mono">
                  {growattPower} W
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* BILAN DE LA VEILLE */}
        <div className={`p-3 rounded-xl border-2 flex-shrink-0 ${
          totalCostYesterday > 0 
            ? 'bg-red-900/30 border-red-500' 
            : 'bg-green-900/30 border-green-500'
        }`} data-testid="daily-summary">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {totalCostYesterday > 0 
                ? <TrendingUp className="w-10 h-10 text-red-400 flex-shrink-0" />
                : <TrendingDown className="w-10 h-10 text-green-400 flex-shrink-0" />
              }
              <div>
                <div className="text-xl font-bold text-white font-mono">
                  BILAN DE LA VEILLE
                </div>
                <div className="text-sm text-gray-400 font-mono">
                  Électricité + Gaz - Solaire
                </div>
              </div>
            </div>
            
            <div className="text-right">
              <div className={`text-5xl font-bold font-mono ${
                totalCostYesterday > 0 ? 'text-red-400' : 'text-green-400'
              }`}>
                {totalCostYesterday > 0 ? '+' : ''}{totalCostYesterday.toFixed(2)}€
              </div>
              <div className="text-lg text-gray-400 font-mono">
                {(elecYesterdayKwh + gasTodayKwh - solarYesterdayKwh).toFixed(1)} kWh net
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Energy;
